import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import Button from '@/shared/components/Button';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import dateFormatService from '@/shared/services/DateFormatService';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import messages from '../employeeMessages';

class RestoreEmployeeModal extends Component {
  state = {
    licenceType: 2, // Default to "New standard license"
    licenseInformation: {},
    licenses: [],
    licenseId: null,
    loaded: false,
    beginDate: moment()
      .utc()
      .startOf('day'),
    endDate: moment()
      .utc()
      .startOf('day')
      .add(1, 'months'),
    months: 1,
    showValidation: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    employee: PropTypes.object,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    saving: false,
    employee: null,
  };

  componentDidMount() {
    if (this.props.show) {
      this.loadLicenseInformation();
    }
  }

  componentDidUpdate(prevProps) {
    // Load license info when modal opens
    if (!prevProps.show && this.props.show) {
      this.loadLicenseInformation();
      this.resetState();
    }
  }

  resetState = () => {
    const today = moment()
      .utc()
      .startOf('day');
    this.setState(
      {
        licenceType: 2,
        licenseId: null,
        loaded: false,
        beginDate: today,
        endDate: moment(today).add(1, 'months'),
        months: 1,
        showValidation: false,
      },
      () => {
        // Apply date constraints after state is set
        this.datesChanged();
      },
    );
  };

  loadLicenseInformation = async () => {
    try {
      const { data } = await api.base.get('/api/license/Info');

      const licenses = (data.temporaryLicenses || [])
        .filter(x => !x.userId)
        .map(x => ({
          ...x,
          name: `${dateFormatService.formatDate(x.beginDate)} - ${dateFormatService.formatDate(
            x.endDate,
          )}`,
        }))
        .sort(dynamicSortMultiple('beginDate', 'endDate'));

      this.setState({
        licenseInformation: data,
        licenses,
        loaded: true,
        // Default to available standard license if available
        licenceType: data.availableLicenses > 0 ? 1 : 2,
      });

      // If not billing per license
      if (!data.billPerLicense) {
        this.handleSave();
      }
    } catch (error) {
      console.error('Error loading license information:', error);
      handleHttpError(this.props.intl)(error);
    }
  };

  handleLicenseTypeChange = e => {
    this.setState({
      licenceType: parseInt(e.target.value),
      licenseId: null, // Reset license selection when type changes
    });
  };

  handleLicenseSelection = e => {
    const selectedValue = e.target.value;
    this.setState({ licenseId: selectedValue || null });
  };

  handleDatesChange = ({ startDate, endDate }) => {
    this.setState(
      {
        beginDate: startDate,
        endDate: endDate,
      },
      () => {
        this.datesChanged();
      },
    );
  };

  datesChanged = () => {
    const { beginDate, endDate } = this.state;

    if (beginDate) {
      // Set min/max constraints
      const minDate = moment(beginDate).add(1, 'months');
      const maxDate = moment(beginDate).add(6, 'months');

      if (endDate) {
        let correctedEndDate = endDate;

        // Auto-correct if outside bounds
        if (endDate.isAfter(maxDate)) {
          correctedEndDate = maxDate;
        }
        if (endDate.isBefore(minDate)) {
          correctedEndDate = minDate;
        }

        // Update state if correction was needed
        if (!correctedEndDate.isSame(endDate)) {
          this.setState({ endDate: correctedEndDate }, () => {
            this.calculateMonths();
          });
        } else {
          this.calculateMonths();
        }
      }
    }
  };

  calculateMonths = () => {
    const { beginDate, endDate } = this.state;
    if (beginDate && endDate) {
      const months = endDate.diff(beginDate, 'months', true);
      this.setState({ months: Math.round(months * 100) / 100 });
    }
  };

  setDuration = months => {
    const { beginDate } = this.state;
    if (beginDate) {
      const endDate = moment(beginDate).add(months, 'months');
      this.setState({ endDate, months }, () => {
        this.datesChanged();
      });
    }
  };

  addMonths = monthsToAdd => {
    const { endDate } = this.state;
    if (endDate) {
      const newEndDate = moment(endDate).add(monthsToAdd, 'months');
      this.setState({ endDate: newEndDate }, () => {
        this.datesChanged();
      });
    }
  };

  isOutsideRange = date => {
    const { beginDate } = this.state;
    const today = moment()
      .utc()
      .startOf('day');

    // Basic constraint: cannot select past dates
    if (date.isBefore(today)) {
      return true;
    }

    if (beginDate) {
      const minDate = moment(beginDate).add(1, 'months');
      const maxDate = moment(beginDate).add(6, 'months');
      return date.isBefore(minDate) || date.isAfter(maxDate);
    }

    // General constraint: max 7 months from today
    const maxDate = moment(today).add(7, 'months');
    return date.isAfter(maxDate);
  };

  // Create a custom duration button group with smart disable logic
  renderDurationButtons = () => {
    const { beginDate, endDate, saving } = this.state;
    const { intl } = this.props;

    // Calculate min/max dates
    const minDate = beginDate ? moment(beginDate).add(1, 'months') : null;
    const maxDate = beginDate ? moment(beginDate).add(6, 'months') : null;

    const durationButtons = [
      {
        action: () => this.addMonths(-1),
        label: messages.SubtractOneMonth,
        disabled: saving || !endDate || (minDate && minDate.isSameOrAfter(endDate)),
      },
      {
        action: () => this.setDuration(1),
        label: `1 ${intl.formatMessage(messages.Months)}`,
        disabled: saving || !beginDate,
      },
      {
        action: () => this.setDuration(3),
        label: `3 ${intl.formatMessage(messages.Months)}`,
        disabled: saving || !beginDate,
      },
      {
        action: () => this.setDuration(6),
        label: `6 ${intl.formatMessage(messages.Months)}`,
        disabled: saving || !beginDate,
      },
      {
        action: () => this.addMonths(1),
        label: messages.AddOneMonth,
        disabled: saving || !endDate || (maxDate && endDate.isSameOrAfter(maxDate)),
      },
    ];

    return (
      <div className="btn-toolbar" role="group">
        <div className="btn-group btn-group-sm" style={{ marginRight: '5px' }}>
          {durationButtons.slice(0, 1).map((button, index) => (
            <button
              key={index}
              type="button"
              className="btn btn-default"
              onClick={button.action}
              disabled={button.disabled}
            >
              {button.label && button.label.id ? (
                <FormattedMessage {...button.label} />
              ) : (
                button.label
              )}
            </button>
          ))}
        </div>
        <div className="btn-group btn-group-sm" style={{ marginRight: '5px' }}>
          {durationButtons.slice(1, 4).map((button, index) => (
            <button
              key={index + 1}
              type="button"
              className="btn btn-default"
              onClick={button.action}
              disabled={button.disabled}
            >
              {button.label && button.label.id ? (
                <FormattedMessage {...button.label} />
              ) : (
                button.label
              )}
            </button>
          ))}
        </div>
        <div className="btn-group btn-group-sm">
          {durationButtons.slice(4, 5).map((button, index) => (
            <button
              key={index + 4}
              type="button"
              className="btn btn-default"
              onClick={button.action}
              disabled={button.disabled}
            >
              {button.label && button.label.id ? (
                <FormattedMessage {...button.label} />
              ) : (
                button.label
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  handleSave = () => {
    const { onSave } = this.props;
    const { licenceType, licenseId, licenses, beginDate, endDate } = this.state;

    const model = {
      license: null,
      buyNewLicense: licenceType === 2 || licenceType === 4,
    };

    if (licenceType === 3) {
      const license = licenses.find(x => x.id === licenseId);
      if (!license || !licenseId || licenseId === '') {
        this.setState({ showValidation: true });
        return;
      }
      model.license = license;
    } else if (licenceType === 4) {
      if (!beginDate || !endDate) {
        this.setState({ showValidation: true });
        return;
      }
      model.license = {
        id: null,
        isTemporary: true,
        beginDate: beginDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }

    onSave(model);
  };

  handleClose = () => {
    this.props.onClose();
  };

  isFormValid = () => {
    const { licenceType, licenseId, beginDate, endDate } = this.state;

    if (licenceType === 3 && (!licenseId || licenseId === '')) return false;
    if (licenceType === 4 && (!beginDate || !endDate)) return false;

    return true;
  };

  renderLicenseTypeOptions = () => {
    const { licenseInformation } = this.state;
    const { intl } = this.props;

    return (
      <div>
        {/* Available standard license */}
        {licenseInformation.availableLicenses > 0 && (
          <div className="radio">
            <label>
              <input
                type="radio"
                name="licenceType"
                value="1"
                checked={this.state.licenceType === 1}
                onChange={this.handleLicenseTypeChange}
                disabled={this.props.saving}
              />
              <FormattedMessage {...messages.AvailableStandardLicense} />
            </label>
          </div>
        )}

        {/* New standard license */}
        <div className="radio">
          <label>
            <input
              type="radio"
              name="licenceType"
              value="2"
              checked={this.state.licenceType === 2}
              onChange={this.handleLicenseTypeChange}
              disabled={this.props.saving}
            />
            <FormattedMessage {...messages.NewStandardLicense} />
          </label>
        </div>

        {/* Available temporary license */}
        {!licenseInformation.isTrial && licenseInformation.availableTemporaryLicenses > 0 && (
          <div className="radio">
            <label>
              <input
                type="radio"
                name="licenceType"
                value="3"
                checked={this.state.licenceType === 3}
                onChange={this.handleLicenseTypeChange}
                disabled={this.props.saving}
              />
              <FormattedMessage {...messages.AvailableTemporaryLicense} />
            </label>
          </div>
        )}

        {/* New temporary license */}
        {!licenseInformation.isTrial && (
          <div className="radio">
            <label>
              <input
                type="radio"
                name="licenceType"
                value="4"
                checked={this.state.licenceType === 4}
                onChange={this.handleLicenseTypeChange}
                disabled={this.props.saving}
              />
              <FormattedMessage {...messages.NewTemporaryLicense} />
            </label>
          </div>
        )}
      </div>
    );
  };

  renderWarningMessage = () => {
    const { licenceType, licenseInformation, months } = this.state;
    const { intl } = this.props;

    if (licenceType === 1) {
      return (
        <div className="alert alert-info">
          <FormattedMessage {...messages.UseUnusedLicenseWarning} />
        </div>
      );
    }

    if (licenceType === 2) {
      return (
        <div className="alert alert-warning">
          <FormattedMessage
            {...messages.NewStandardLicenseWarning}
            values={{
              price: intl.formatNumber(licenseInformation.nextLicensePrice || 0, {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              newTotalLicenses: (licenseInformation.totalLicenses || 0) + 1,
            }}
          />
        </div>
      );
    }

    if (licenceType === 4) {
      const totalCost = (licenseInformation.temporaryLicensePrice || 0) * months;
      return (
        <div className="alert alert-warning">
          <FormattedMessage
            {...messages.NewTemporaryLicenseWarning}
            values={{
              months: intl.formatNumber(months, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }),
              price: intl.formatNumber(totalCost, {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }}
          />
        </div>
      );
    }

    return null;
  };

  render() {
    const { show, saving, employee, intl } = this.props;
    const {
      loaded,
      licenceType,
      licenses,
      licenseId,
      beginDate,
      endDate,
      showValidation,
    } = this.state;

    if (!employee || !loaded) return null;

    const selectLicenseMessage = intl.formatMessage(messages.SelectLicense);
    return (
      <Modal show={show} onHide={this.handleClose} dialogClassName="modal-lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.RestoreEmployee} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-horizontal">
            {/* License Type Selection */}
            <div className="form-group">
              <div className="col-md-3">
                <label>
                  <FormattedMessage {...messages.LicenseType} />
                </label>
              </div>
              <div className="col-md-9">{this.renderLicenseTypeOptions()}</div>
            </div>

            {/* Available Temporary License Selection */}
            {licenceType === 3 && (
              <div className="form-group">
                <div className="col-md-3">
                  <label>
                    <FormattedMessage {...messages.TemporaryLicense} />
                  </label>
                </div>
                <div className="col-md-9">
                  <select
                    className="form-control"
                    value={licenseId ? licenseId : ''}
                    onChange={this.handleLicenseSelection}
                    disabled={saving}
                    defaultValue={intl.formatMessage(messages.SelectLicense)}
                  >
                    {!licenseId && (
                      <option key="SelectLicense" value={selectLicenseMessage}>
                        {selectLicenseMessage}
                      </option>
                    )}
                    {licenses.map(license => (
                      <option key={license.id} value={license.id}>
                        {license.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* New Temporary License Date Selection */}
            {licenceType === 4 && (
              <>
                <div className="form-group">
                  <div className="col-md-3">
                    <label>
                      <FormattedMessage {...messages.Period} />
                    </label>
                  </div>
                  <div className="col-md-9">
                    <DelayedDateRangePicker
                      startDate={beginDate}
                      endDate={endDate}
                      onDatesChange={this.handleDatesChange}
                      isOutsideRange={this.isOutsideRange}
                      block
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Duration Buttons */}
                <div className="form-group">
                  <div className="col-md-9 col-md-offset-3">{this.renderDurationButtons()}</div>
                </div>

                <hr />
              </>
            )}
            {/* Warning Messages */}
            {this.renderWarningMessage() && (
              <div className="form-group">
                <div className="col-md-12">{this.renderWarningMessage()}</div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleClose} disabled={saving}>
            <FormattedMessage {...messages.Cancel} />
          </Button>
          <Button
            onClick={this.handleSave}
            disabled={saving || (showValidation && !this.isFormValid())}
            btnClassName="btn-inverse"
            showProgress={saving}
            onMouseOver={() => this.setState({ showValidation: true })}
          >
            <FormattedMessage {...messages.Add} />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default injectIntl(RestoreEmployeeModal);
